import React, { useMemo } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import useOfficersData from '../../../utils/useOfficersData';
import './officersdisplay.css';

const OfficersDisplay = ({ 
  branchNmlsId = null, 
  branchManagerEmail = null, 
  onShowLeadForm = () => {}, 
  filteredOfficers = null,
  isSearchResult = false
}) => {
  // Use the custom hook to fetch officers data
  const { filteredOfficers: fetchedOfficers, isLoading, error } = useOfficersData(branchNmlsId);
  
  // If filteredOfficers is directly provided (search results)
  // use those instead of the fetched data
  const officers = filteredOfficers || fetchedOfficers || [];
  
  // Only split into managers and team members if not displaying search results
  const { managers, teamMembers } = useMemo(() => {
    if (isSearchResult || !officers || officers.length === 0) {
      return { managers: [], teamMembers: officers || [] };
    }

    const managers = officers.filter(officer => {
      if (branchManagerEmail && officer.email && branchManagerEmail.includes(officer.email)) {
        return true;
      }
      return (
        officer.title?.includes('Director') ||
        officer.title?.includes('President') ||
        officer.title?.includes('Manager') ||
        officer.title?.includes('Assistant Vice President')
      );
    });

    const teamMembers = officers.filter(officer => {
      if (branchManagerEmail && officer.email && branchManagerEmail.includes(officer.email)) {
        return false;
      }
      return !(
        officer.title?.includes('Manager') ||
        officer.title?.includes('Director') ||
        officer.title?.includes('President') ||
        officer.title?.includes('Assistant Vice President')
      );
    });

    return { managers, teamMembers };
  }, [officers, branchManagerEmail, isSearchResult]);

  if (isLoading) {
    return <div className="od_loaderContainer"><div className="od_loader"></div></div>;
  }

  if (error) {
    return <div className="od_errorContainer">Error loading officers data: {error.message}</div>;
  }

  // For search results, display all officers in a single list
  if (isSearchResult) {
    return (
      <div>
        <Row>
          {officers.map(officer => (
            <Col md={6} sm={12} xs={12} lg={4} key={officer.loID || `officer-${officer.name}`}>
              <div 
                className="od_personCard" 
                onClick={() => onShowLeadForm(officer.loID)}
                style={{ cursor: 'pointer' }}
              >
                <div className="od_officerImgDiv">
                  <img
                    src={`${officer.profileUrl}/images/${officer.loPhoto}`}
                    alt={officer.name}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/150';
                    }}
                  />
                </div>
                <h6><strong>{officer.name}</strong></h6>
                <p>{officer.title}<br />NMLS ID: {officer.nmls}</p>
                <p>{officer.email}</p>
                <Button 
                  href={officer.profileUrl} 
                  target="_blank" 
                  className="od_button"
                  onClick={(e) => e.stopPropagation()} // Prevent card click
                >
                  See Full Bio
                </Button>
                <Button 
                  className="od_button" 
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent duplicate modal trigger
                    onShowLeadForm(officer.loID);
                  }}
                >
                  Apply Online
                </Button>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  // For branch display, use the standard managers/team members view
  return (
    <>
      {managers.length > 0 && (
        <div>
          <div className="od_headerBorder" />
          <h2 style={{fontWeight:600}}>Managers</h2>
          <Row>
            {managers.map(manager => (
              <Col md={6} sm={12} xs={12} lg={4} key={manager.loID || `manager-${manager.name}`}>
                <div 
                  className="od_personCard od_personCardManager"
                  onClick={() => onShowLeadForm(manager.loID)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="od_officerImgDiv">
                    <img
                      src={`${manager.profileUrl}/images/${manager.loPhoto}`}
                      alt={manager.name}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/150';
                      }}
                    />
                  </div>
                  <h6><strong>{manager.name}</strong></h6>
                  <p>{manager.title}<br />NMLS ID: {manager.nmls}</p>
                  <p>{manager.email}</p>
                  <Button 
                    href={manager.profileUrl} 
                    target="_blank" 
                    className="od_button od_buttonWhite"
                    onClick={(e) => e.stopPropagation()} // Prevent card click
                  >
                    See Full Bio
                  </Button>
                  <Button 
                    className="od_button od_buttonWhite" 
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent duplicate modal trigger
                      onShowLeadForm(manager.loID);
                    }}
                  >
                    Apply Online
                  </Button>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      )}

      {teamMembers.length > 0 && (
        <div>
          <div className="od_headerBorder" />
          <h2 style={{fontWeight:600}}>Meet Our Team</h2>
          <br />
          <Row>
            {teamMembers.map(member => (
              <Col md={6} sm={12} xs={12} lg={4} key={member.loID || `member-${member.name}`}>
                <div 
                  className="od_personCard"
                  onClick={() => onShowLeadForm(member.loID)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="od_officerImgDiv">
                    <img
                      src={`${member.profileUrl}/images/${member.loPhoto}`}
                      alt={member.name}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/150';
                      }}
                    />
                  </div>
                  <h6><strong>{member.name}</strong></h6>
                  <p>{member.title}<br />NMLS ID: {member.nmls}</p>
                  <p>{member.email}</p>
                  <Button 
                    href={member.profileUrl} 
                    target="_blank" 
                    className="od_button"
                    onClick={(e) => e.stopPropagation()} // Prevent card click
                  >
                    See Full Bio
                  </Button>
                  <Button 
                    className="od_button" 
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent duplicate modal trigger
                      onShowLeadForm(member.loID);
                    }}
                  >
                    Apply Online
                  </Button>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      )}
    </>
  );
};

export default OfficersDisplay;